import Link from 'next/link'
import SwiperCore, { Navigation, Pagination, EffectFade } from 'swiper'
import Cta from '../styles/Cta'
import styled, { css } from 'styled-components'
import Grid, { Cell } from '../styles/Grid'
import { Item, Content } from '../styles/Banner'
import { Component, FadeInArea } from '../styles/Component'
import { Parallax, ParallaxProvider } from 'react-scroll-parallax'
import { useInView } from 'react-intersection-observer'
import { breakpoint } from '../layout/styles'

SwiperCore.use([Navigation, Pagination, EffectFade]);

const imgSrc = (url: string, width: number) => {
  if (!url) return url
  if (url.toLowerCase().includes('.gif')) return url
  return `${url}?width=${width}&auto=webp&format=png`
}

const imgSrcSet = (url: string, width: number) => {
  if (!url) return undefined
  if (url.toLowerCase().includes('.gif')) return undefined
  return `${url}?width=${width}&dpr=2&auto=webp&format=png 2x`
}

const ProductBanner = ({ item }: any) => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 })

  const product = item.product?.[0]
  const image = product.images?.[0]
  const left = item.text_alignment === 'Left'
  const ingredient1 = product.ingredients?.ingredient1
  const ingredient2 = product.ingredients?.ingredient2
  const ingredient3 = product.ingredients?.ingredient3

  return (
    <div>
      <Component ref={ref}>
        <ParallaxProvider>
          <Item
            light={item.text_style === 'Light'}
            left={left}
            variant="small"
            style={{ background: product.color?.[0].background }}>
            <Wrapper variant="ingredient" inView={inView}>
              <Grid style={{ flexDirection: left && 'row-reverse' }}>
                <Cell md={9} gap={left ? 2 : 0} style={{ position: 'relative' }}>
                  {ingredient1 &&
                    <Ingredient1 y={[10, 0]} x={[-10, 0]} tagOuter="div">
                      <img src={imgSrc(ingredient1.url, 475)}
                        srcSet={imgSrcSet(ingredient1.url, 475)}
                        width={475}
                        height={200}
                        alt={ingredient1.description || 'Ingredient'} />
                    </Ingredient1>}
                  <Parallax y={[5, -5]} x={[0, 0]} tagOuter="div">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {image &&
                        <ProductImage
                          src={imgSrc(image.url, 525)}
                          srcSet={imgSrcSet(image.url, 525)}
                          alt={image.description || "Image"}
                          width={525}
                          height={525}
                          style={{ maxWidth: '100%' }} />}
                    </div>
                  </Parallax>
                  {ingredient2 &&
                    <Ingredient2 y={[0, 10]} x={[-10, 0]} tagOuter="div">
                      <img src={imgSrc(ingredient2.url, 475)}
                        srcSet={imgSrcSet(ingredient2.url, 475)}
                        alt={ingredient2.description || 'Ingredient'}
                        width={475}
                        height={200} />
                    </Ingredient2>}
                  {ingredient3 &&
                    <Ingredient3 y={[0, 0]} x={[10, -10]} tagOuter="div">
                      <img src={imgSrc(ingredient3.url, 475)}
                        srcSet={imgSrcSet(ingredient3.url, 475)}
                        alt={ingredient3.description || 'Ingredient'}
                        width={475}
                        height={200} />
                    </Ingredient3>}
                </Cell>
                <Cell md={3} gap={left ? 0 : 2}>
                  <Container>
                    <div>
                      <h2>{item.title}</h2>
                      <p>{item.description}</p>
                      {item.cta.href &&
                        <Link href={item.cta.href}>
                          <Cta href="" light={item.text_style === 'Light'}>{item.cta.title}</Cta>
                        </Link>}
                    </div>
                  </Container>
                </Cell>
              </Grid>
            </Wrapper>
          </Item>
        </ParallaxProvider>
      </Component>
    </div>
  )
}
export default ProductBanner

const ProductImage = styled.img<any>`
  height: auto !important;
`

const Ingredient = styled<any>(Parallax)`
  position: absolute;
  pointer-events: none;
  img {
    max-width: 60vw;
  }
`

const Ingredient1 = styled(Ingredient)`
  bottom: 0;
  left: -120px;
  z-index: 1;
`

const Ingredient2 = styled(Ingredient)`
  bottom: 100px;
  left: -120px;
  z-index: -1;
`

const Ingredient3 = styled(Ingredient)`
  bottom: -20px;
  right: -120px;
`
const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  h2 {      
    margin-top: 2rem;
    @media ${breakpoint.desktop} {
      margin-top: 0;
    }
  }
`
const Wrapper = styled<any>(FadeInArea)`
  position: relative;
  width: 100%;
  padding: 3rem 1.5rem;
  @media ${breakpoint.desktop} {
    padding: 0;
  }
  ${props => (!props.variant || props.variant !== 'full') && css`
    max-width: 1120px;
    margin: 0 auto;
  `}
`